import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import CustomSelect from '../../../components/widgets/CustomSelect';

/**
 * Footer 테마 제어 컴포넌트
 * 원본: footer-control.html
 */
const FooterControl = () => {
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || {};
  const set = useMemo(() => themeContext?.set || (() => {}), [themeContext?.set]);

  // React Hooks는 항상 동일한 순서로 호출되어야 하므로, 조건부 return 전에 선언
  const copyrightBackgroundColorInputRef = useRef(null);
  const copyrightBackgroundColorTextInputRef = useRef(null);
  const copyrightTextColorInputRef = useRef(null);
  const copyrightTextColorTextInputRef = useRef(null);
  const copyrightHeightInputRef = useRef(null);

  const defaultFooterTheme = 'simple';
  const defaultCopyrightAlignment = 'center';
  const defaultCopyrightBackgroundColor = '#343a40';
  const defaultCopyrightTextColor = '#ffffff';
  const defaultCopyrightHeight = 20;

  /**
   * RGB/RGBA 색상을 HEX로 변환
   * @param {string} rgbString - 'rgb(52, 58, 64)' 또는 'rgba(52, 58, 64, 1)' 형식
   * @returns {string} HEX 색상 (#343a40)
   */
  const rgbToHex = (rgbString) => {
    if (!rgbString || rgbString === 'transparent' || rgbString === 'rgba(0, 0, 0, 0)') {
      return null;
    }
    const rgb = rgbString.match(/\d+/g);
    if (rgb && rgb.length >= 3) {
      return '#' + rgb.slice(0, 3).map(function(x) {
        const hex = parseInt(x, 10).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      }).join('');
    }
    return null;
  };

  /**
   * 실제 적용된 Copyright 색상 가져오기 (computed style)
   * @returns {object} { backgroundColor, textColor }
   */
  const getActualCopyrightColors = () => {
    const copyrightContainer = document.querySelector('.footer-area .bg-gray-3');
    const copyrightP = copyrightContainer ? copyrightContainer.querySelector('.copyright p') : null;

    let backgroundColor = defaultCopyrightBackgroundColor;
    let textColor = defaultCopyrightTextColor;

    if (copyrightContainer) {
      const computedBg = window.getComputedStyle(copyrightContainer).backgroundColor;
      const hexBg = rgbToHex(computedBg);
      if (hexBg) {
        backgroundColor = hexBg;
      }
    }

    if (copyrightP) {
      const computedColor = window.getComputedStyle(copyrightP).color;
      const hexColor = rgbToHex(computedColor);
      if (hexColor) {
        textColor = hexColor;
      }
    }

    return { backgroundColor, textColor };
  };

  /**
   * Footer 테마 업데이트
   */
  const updateFooterTheme = (theme) => {
    try {
      set('footerTheme', theme);

      // 테마 변경 후 실제 적용된 색상 가져와서 입력 필드 업데이트
      setTimeout(() => {
        requestAnimationFrame(() => {
          const actualColors = getActualCopyrightColors();

          // 배경색 업데이트
          if (actualColors.backgroundColor && copyrightBackgroundColorInputRef.current && copyrightBackgroundColorTextInputRef.current) {
            copyrightBackgroundColorInputRef.current.value = actualColors.backgroundColor;
            copyrightBackgroundColorTextInputRef.current.value = actualColors.backgroundColor;
            set('copyrightBackgroundColor', actualColors.backgroundColor);
          }

          // 글자색 업데이트
          if (actualColors.textColor && copyrightTextColorInputRef.current && copyrightTextColorTextInputRef.current) {
            copyrightTextColorInputRef.current.value = actualColors.textColor;
            copyrightTextColorTextInputRef.current.value = actualColors.textColor;
            set('copyrightTextColor', actualColors.textColor);
          }
        });
      }, 100); // CSS 적용 대기 시간
    } catch (e) {
      console.error('Footer 테마 업데이트 중 오류:', e);
    }
  };

  /**
   * Copyright 정렬 업데이트
   */
  const updateCopyrightAlignment = (alignment) => {
    try {
      set('copyrightAlignment', alignment);
    } catch (e) {
      console.error('Copyright 정렬 업데이트 중 오류:', e);
    }
  };

  /**
   * Copyright 배경색 적용
   */
  const applyCopyrightBackgroundColor = useCallback((colorValue) => {
    if (!colorValue || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(colorValue)) {
      return;
    }

    // 동적 스타일 태그를 생성하여 모든 테마에서 !important 규칙을 덮어씀
    let styleTag = document.getElementById('copyright-background-color-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'copyright-background-color-style';
      document.head.appendChild(styleTag);
    }
    // 모든 Footer 테마에서 적용되도록 높은 우선순위로 설정
    styleTag.textContent = `
      .footer-area .bg-gray-3 {
        background: ${colorValue} !important;
        background-color: ${colorValue} !important;
      }
      .footer-area.footer-theme-simple .bg-gray-3,
      .footer-area.footer-theme-modern .bg-gray-3,
      .footer-area.footer-theme-vibrant .bg-gray-3 {
        background: ${colorValue} !important;
        background-color: ${colorValue} !important;
      }
    `;

    set('copyrightBackgroundColor', colorValue);
  }, [set]);

  /**
   * Copyright 글자색 적용
   */
  const applyCopyrightTextColor = useCallback((colorValue) => {
    if (!colorValue || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(colorValue)) {
      return;
    }
    const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
    const copyrightP = copyrightEl ? copyrightEl.querySelector('p') : null;
    if (copyrightEl) {
      copyrightEl.style.color = colorValue;
    }
    if (copyrightP) {
      copyrightP.style.color = colorValue;
    }
    set('copyrightTextColor', colorValue);
  }, [set]);

  /**
   * Copyright 높이 적용
   */
  const applyCopyrightHeight = useCallback((heightValue) => {
    const numValue = parseInt(heightValue, 10);
    if (isNaN(numValue) || numValue < 0 || numValue > 200) {
      return;
    }
    const heightPx = numValue + 'px';
    const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
    if (copyrightEl) {
      copyrightEl.style.paddingTop = heightPx;
      copyrightEl.style.paddingBottom = heightPx;
    }
    set('copyrightHeight', numValue);
  }, [set]);

  // 초기화: 기본값 설정 및 이벤트 리스너 등록
  useEffect(() => {
    // Footer 테마/정렬은 controlled value로 렌더링되므로 별도 ref 동기화 불필요

    // Copyright 배경색 초기화
    const initialBgColor = themeConfig.copyrightBackgroundColor || defaultCopyrightBackgroundColor;
    if (copyrightBackgroundColorInputRef.current && copyrightBackgroundColorTextInputRef.current) {
      copyrightBackgroundColorInputRef.current.value = initialBgColor;
      copyrightBackgroundColorTextInputRef.current.value = initialBgColor;
      applyCopyrightBackgroundColor(initialBgColor);
    }

    // Copyright 글자색 초기화
    const initialTextColor = themeConfig.copyrightTextColor || defaultCopyrightTextColor;
    if (copyrightTextColorInputRef.current && copyrightTextColorTextInputRef.current) {
      copyrightTextColorInputRef.current.value = initialTextColor;
      copyrightTextColorTextInputRef.current.value = initialTextColor;
      applyCopyrightTextColor(initialTextColor);
    }

    // Copyright 높이 초기화
    const initialHeight = themeConfig.copyrightHeight !== undefined && themeConfig.copyrightHeight !== null
      ? parseInt(themeConfig.copyrightHeight, 10)
      : defaultCopyrightHeight;
    if (copyrightHeightInputRef.current) {
      copyrightHeightInputRef.current.value = initialHeight;
      applyCopyrightHeight(initialHeight);
    }
  }, [
    themeConfig.footerTheme,
    themeConfig.copyrightAlignment,
    themeConfig.copyrightBackgroundColor,
    themeConfig.copyrightTextColor,
    themeConfig.copyrightHeight,
    applyCopyrightBackgroundColor,
    applyCopyrightTextColor,
    applyCopyrightHeight
  ]);

  // Help 버튼 hover 핸들러
  const handleHelpMouseOver = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
    e.currentTarget.style.borderColor = 'var(--theme-color-primary)';
    e.currentTarget.style.color = 'var(--theme-color-primary)';
  };

  const handleHelpMouseOut = (e) => {
    e.currentTarget.style.backgroundColor = 'transparent';
    e.currentTarget.style.borderColor = '#ddd';
    e.currentTarget.style.color = '#666';
  };

  // 리셋 버튼 hover 핸들러
  const handleResetMouseOver = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
    e.currentTarget.style.borderColor = 'var(--theme-color-primary)';
  };

  const handleResetMouseOut = (e) => {
    e.currentTarget.style.backgroundColor = 'transparent';
    e.currentTarget.style.borderColor = '#ddd';
  };

  return (
    <>
      <style>
        {`
          #footer-theme-control-section,
          #copyright-alignment-control-section,
          #copyright-background-color-control-section,
          #copyright-text-color-control-section,
          #copyright-height-control-section:not(:last-child) {
            padding-bottom: 15px;
          }
          .footer-theme-control-header,
          .copyright-alignment-control-header {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 8px;
          }
          .footer-theme-control-header > label,
          .copyright-alignment-control-header > label {
            display: block;
            font-weight: bold;
            color: #333;
            font-size: 13px;
            cursor: default;
            margin: 0;
            flex: 1;
          }
          #footer-theme-control-section .theme-help-btn,
          #copyright-alignment-control-section .theme-help-btn {
            background: transparent;
            border: 1px solid #ddd;
            color: #666;
            font-size: 11px;
            width: 20px;
            height: 20px;
            padding: 0;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
            flex-shrink: 0;
          }
          #footer-theme-selector,
          #copyright-alignment-selector {
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 13px;
            width: 100%;
            background: white;
            cursor: pointer;
          }
          .copyright-background-color-control-header,
          .copyright-text-color-control-header,
          .copyright-height-control-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 6px;
          }
          .copyright-background-color-control-header > label,
          .copyright-text-color-control-header > label,
          .copyright-height-control-header > label {
            font-size: 12px;
            color: #666;
            font-weight: 500;
            cursor: pointer;
          }
          #reset-copyright-background-color-btn,
          #reset-copyright-text-color-btn,
          #reset-copyright-height-btn {
            background: transparent;
            border: 1px solid #ddd;
            color: #666;
            font-size: 10px;
            padding: 2px 8px;
            border-radius: 3px;
            cursor: pointer;
            transition: all 0.2s;
          }
          .copyright-background-color-input-container,
          .copyright-text-color-input-container {
            display: flex;
            gap: 8px;
            align-items: center;
          }
          #copyright-background-color-input,
          #copyright-text-color-input {
            width: 60px;
            height: 40px;
            border: 1px solid #ddd;
            border-radius: 4px;
            cursor: pointer;
            padding: 2px;
          }
          #copyright-background-color-text-input,
          #copyright-text-color-text-input {
            flex: 1;
            padding: 8px 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 12px;
            font-family: monospace;
          }
          .copyright-background-color-description,
          .copyright-text-color-description,
          .copyright-height-description {
            font-size: 10px;
            color: #999;
            margin-top: 4px;
            line-height: 1.4;
          }
          .copyright-height-input-container {
            position: relative;
          }
          #copyright-height-input {
            width: 100%;
            padding: 6px 10px;
            padding-right: 35px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 12px;
            font-family: monospace;
            text-align: center;
          }
          .copyright-height-unit {
            position: absolute;
            right: 8px;
            bottom: 6px;
            font-size: 11px;
            color: #999;
            pointer-events: none;
          }
        `}
      </style>

      {/* Footer 테마 선택 */}
      <div id="footer-theme-control-section">
        <div className="footer-theme-control-header">
          <label htmlFor="footer-theme-selector">🎨 Footer 디자인 테마:</label>
          <button
            type="button"
            className="theme-help-btn"
            data-target="footer-theme-control-section"
            title="이 항목으로 이동"
            onMouseOver={handleHelpMouseOver}
            onMouseOut={handleHelpMouseOut}
          >
            ?
          </button>
        </div>
        <CustomSelect
          inputId="footer-theme-selector"
          value={themeConfig.footerTheme || defaultFooterTheme}
          onChange={(v) => updateFooterTheme(v)}
          isSearchable={false}
          isClearable={false}
          options={[
            { value: 'simple', label: '1) 심플한 컨셉 (Urdan 스타일)' },
            { value: 'modern', label: '2) 모던한 컨셉' },
            { value: 'vibrant', label: '3) 화려한 컨셉' },
            { value: 'glow', label: '4) GLOW 검정 테마' },
          ]}
          styles={{
            control: (base, state) => ({
              ...base,
              width: '100%',
              minHeight: 36,
              fontSize: 12,
              borderRadius: 4,
              borderColor: state.isFocused ? '#86b7fe' : '#ddd',
            }),
            valueContainer: (base) => ({ ...base, padding: '0 10px' }),
            indicatorsContainer: (base) => ({ ...base, height: 36 }),
          }}
        />
      </div>

      {/* Copyright 정렬 선택 */}
      <div id="copyright-alignment-control-section">
        <div className="copyright-alignment-control-header">
          <label htmlFor="copyright-alignment-selector">📐 Copyright 정렬:</label>
          <button
            type="button"
            className="theme-help-btn"
            data-target="copyright-alignment-control-section"
            title="이 항목으로 이동"
            onMouseOver={handleHelpMouseOver}
            onMouseOut={handleHelpMouseOut}
          >
            ?
          </button>
        </div>
        <CustomSelect
          inputId="copyright-alignment-selector"
          value={themeConfig.copyrightAlignment || defaultCopyrightAlignment}
          onChange={(v) => updateCopyrightAlignment(v)}
          isSearchable={false}
          isClearable={false}
          options={[
            { value: 'left', label: '좌측 정렬' },
            { value: 'center', label: '가운데 정렬' },
            { value: 'right', label: '우측 정렬' },
          ]}
          styles={{
            control: (base, state) => ({
              ...base,
              width: '100%',
              minHeight: 36,
              fontSize: 12,
              borderRadius: 4,
              borderColor: state.isFocused ? '#86b7fe' : '#ddd',
            }),
            valueContainer: (base) => ({ ...base, padding: '0 10px' }),
            indicatorsContainer: (base) => ({ ...base, height: 36 }),
          }}
        />
      </div>

      {/* Copyright 배경색 제어 */}
      <div id="copyright-background-color-control-section">
        <div className="copyright-background-color-control-header">
          <label htmlFor="copyright-background-color-input">Copyright 배경색:</label>
          <button
            type="button"
            id="reset-copyright-background-color-btn"
            onMouseOver={handleResetMouseOver}
            onMouseOut={handleResetMouseOut}
            onClick={() => {
              const defaultColor = defaultCopyrightBackgroundColor;
              if (copyrightBackgroundColorInputRef.current && copyrightBackgroundColorTextInputRef.current) {
                copyrightBackgroundColorInputRef.current.value = defaultColor;
                copyrightBackgroundColorTextInputRef.current.value = defaultColor;
                applyCopyrightBackgroundColor(defaultColor);
              }
            }}
          >
            리셋
          </button>
        </div>
        <div className="copyright-background-color-input-container">
          <input
            type="color"
            id="copyright-background-color-input"
            ref={copyrightBackgroundColorInputRef}
            defaultValue={defaultCopyrightBackgroundColor}
            onChange={(e) => {
              const color = e.target.value;
              if (copyrightBackgroundColorTextInputRef.current) {
                copyrightBackgroundColorTextInputRef.current.value = color;
              }
              applyCopyrightBackgroundColor(color);
            }}
          />
          <input
            type="text"
            id="copyright-background-color-text-input"
            ref={copyrightBackgroundColorTextInputRef}
            placeholder={defaultCopyrightBackgroundColor}
            onChange={(e) => {
              const color = e.target.value;
              if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
                if (copyrightBackgroundColorInputRef.current) {
                  copyrightBackgroundColorInputRef.current.value = color;
                }
                applyCopyrightBackgroundColor(color);
              }
            }}
            onBlur={(e) => {
              const color = e.target.value;
              if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
                const currentColor = themeConfig.copyrightBackgroundColor || defaultCopyrightBackgroundColor;
                e.target.value = currentColor;
                if (copyrightBackgroundColorInputRef.current) {
                  copyrightBackgroundColorInputRef.current.value = currentColor;
                }
                applyCopyrightBackgroundColor(currentColor);
              }
            }}
          />
        </div>
        <div className="copyright-background-color-description">
          Copyright 영역의 배경색을 설정합니다 (HEX 형식).
        </div>
      </div>

      {/* Copyright 글자색 제어 */}
      <div id="copyright-text-color-control-section">
        <div className="copyright-text-color-control-header">
          <label htmlFor="copyright-text-color-input">Copyright 글자색:</label>
          <button
            type="button"
            id="reset-copyright-text-color-btn"
            onMouseOver={handleResetMouseOver}
            onMouseOut={handleResetMouseOut}
            onClick={() => {
              const defaultColor = defaultCopyrightTextColor;
              if (copyrightTextColorInputRef.current && copyrightTextColorTextInputRef.current) {
                copyrightTextColorInputRef.current.value = defaultColor;
                copyrightTextColorTextInputRef.current.value = defaultColor;
                applyCopyrightTextColor(defaultColor);
              }
            }}
          >
            리셋
          </button>
        </div>
        <div className="copyright-text-color-input-container">
          <input
            type="color"
            id="copyright-text-color-input"
            ref={copyrightTextColorInputRef}
            defaultValue={defaultCopyrightTextColor}
            onChange={(e) => {
              const color = e.target.value;
              if (copyrightTextColorTextInputRef.current) {
                copyrightTextColorTextInputRef.current.value = color;
              }
              applyCopyrightTextColor(color);
            }}
          />
          <input
            type="text"
            id="copyright-text-color-text-input"
            ref={copyrightTextColorTextInputRef}
            placeholder={defaultCopyrightTextColor}
            onChange={(e) => {
              const color = e.target.value;
              if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
                if (copyrightTextColorInputRef.current) {
                  copyrightTextColorInputRef.current.value = color;
                }
                applyCopyrightTextColor(color);
              }
            }}
            onBlur={(e) => {
              const color = e.target.value;
              if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
                const currentColor = themeConfig.copyrightTextColor || defaultCopyrightTextColor;
                e.target.value = currentColor;
                if (copyrightTextColorInputRef.current) {
                  copyrightTextColorInputRef.current.value = currentColor;
                }
                applyCopyrightTextColor(currentColor);
              }
            }}
          />
        </div>
        <div className="copyright-text-color-description">
          Copyright 영역의 글자색을 설정합니다 (HEX 형식).
        </div>
      </div>

      {/* Copyright 높이 제어 */}
      <div id="copyright-height-control-section">
        <div className="copyright-height-control-header">
          <label htmlFor="copyright-height-input">Copyright 높이 (상하 패딩):</label>
          <button
            type="button"
            id="reset-copyright-height-btn"
            onMouseOver={handleResetMouseOver}
            onMouseOut={handleResetMouseOut}
            onClick={() => {
              const defaultHeight = defaultCopyrightHeight;
              if (copyrightHeightInputRef.current) {
                copyrightHeightInputRef.current.value = defaultHeight;
                applyCopyrightHeight(defaultHeight);
              }
            }}
          >
            리셋
          </button>
        </div>
        <div className="copyright-height-input-container">
          <input
            type="number"
            id="copyright-height-input"
            ref={copyrightHeightInputRef}
            step="1"
            min="0"
            max="200"
            defaultValue={defaultCopyrightHeight}
            placeholder="20"
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value) && value >= 0 && value <= 200) {
                applyCopyrightHeight(value);
              }
            }}
            onBlur={(e) => {
              const value = parseInt(e.target.value, 10);
              if (isNaN(value) || value < 0 || value > 200) {
                const currentValue = themeConfig.copyrightHeight !== undefined && themeConfig.copyrightHeight !== null
                  ? parseInt(themeConfig.copyrightHeight, 10)
                  : defaultCopyrightHeight;
                e.target.value = currentValue;
                applyCopyrightHeight(currentValue);
              }
            }}
          />
          <div className="copyright-height-unit">px</div>
        </div>
        <div className="copyright-height-description">
          Copyright 영역의 상단과 하단에 동일한 패딩이 적용됩니다.
        </div>
      </div>
    </>
  );
};

export default FooterControl;
